import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import convertLayoutPositionToStyles from 'owa-calendar-surface-dategrid/lib//utils/convertLayoutPositionToStyles';
import type {
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    SurfaceItem,
} from 'owa-calendar-surface-dategrid';
import { getHorizontalLayout } from 'owa-calendar-surface-dategrid/lib/logic/getHorizontalLayout';
import type { LayoutPositionDetails } from 'owa-calendar-surface-dategrid/lib/interfaces/LayoutPositionDetails';
import { unfoldSurfaceItem } from '../logic/unfoldSurfaceItem';
import { wrapToGridItem } from 'owa-calendar-surface-dategrid/lib//surfaceItems/GridItem';

/**
 * Transforms the specified surface item into one or more JSX elements applying unfolding if needed
 * @param store The surface store
 * @param config the configuration for calendar day/week view
 * @param item The surface item to transform
 * @param unfoldedItemFactory The factory to call to create the unfolded SurfaceItem objects
 * @returns One or more JSX elements for each unfolded instance of the surface item
 */
export function transformSurfaceItem(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    item: SurfaceItem,
    unfoldedItemFactory: (item: CalendarEvent, itemIndex: number) => SurfaceItem
): JSX.Element[] {
    let transformedItems: JSX.Element[] = [];

    if (item) {
        // 1. Get the unfolded SurfaceItem objects
        unfoldSurfaceItem(item, config.columnProvider.columnCount, store.dayLength).map(
            (unfoldedItem, index) => {
                // 2. Get top and height position for the unfolded component
                let topHeightLayout: LayoutPositionDetails = config.itemsLayout.getVerticalLayout(
                    unfoldedItem
                );

                // 3. Get left and right position for the unfolded component
                let leftRightLayout: LayoutPositionDetails = getHorizontalLayout(
                    unfoldedItem,
                    config.columnProvider,
                    store.dayLength,
                    store.userOptions.isRTL
                );

                let itemLayout = { ...topHeightLayout, ...leftRightLayout };
                let unfolded = unfoldedItemFactory(item.model, index);

                let itemConfig = config.getItemConfig(item.model);

                let unfoldedGridItem = wrapToGridItem(
                    convertLayoutPositionToStyles(itemLayout),
                    unfolded.key,
                    itemConfig,
                    unfolded.model,
                    itemConfig?.template?.(store, unfoldedItem.model),
                    null /* itemIndex */,
                    item.key /* tabId */
                );

                transformedItems.push(unfoldedGridItem);
            }
        );
    }
    return transformedItems;
}
